import { ref } from "vue";
import { useRequestURL } from "#app";
import { useImage, useRuntimeConfig } from "#imports";
import { IMAGE_QUALITY } from "~/utils/imageSizes";

/* ====================================================================
 *  useImageOptimizer — спільна обв'язка @nuxt/image (п.9в, п.9г).
 *
 *  Тут зібрані ТРИ пастки, кожна з яких коштувала окремого розслідування,
 *  і які інакше довелось би дублювати в кожного споживача (обкладинки
 *  карток, галерея товару).
 *
 *  1. ЯКИЙ URL ВІДДАВАТИ ОПТИМІЗАТОРУ. Провайдеру `vercel` потрібен
 *     ВІДНОСНИЙ шлях: абсолютний із чужим хостом (а на прев'ю-деплої
 *     origin саме такий) не пройде `image.domains`, і @nuxt/image ТИХО
 *     поверне URL без оптимізації. Локальному `ipx` — навпаки, потрібен
 *     абсолютний: шлях із провідним слешем він шукає серед файлів
 *     public/, а `/api/img/` — це серверний роут.
 *
 *  2. ОПТИМІЗАЦІЇ МОЖЕ НЕ БУТИ ЗОВСІМ, І МОВЧКИ. Коли @nuxt/image не
 *     береться за URL, він повертає вхід незміненим — без помилки. Так
 *     буває локально, коли дев-сервер зайняв не той порт:
 *     `validateDomains` звіряє host РАЗОМ із портом, а в `image.domains`
 *     порт вказати не можна, бо цей самий список їде у Vercel build
 *     config. Ловимо це порівнянням `out === src`: інакше вийшов би
 *     srcset із однакових URL і брехливими дескрипторами — рівно та
 *     хвороба, що лікувалась у п.9в.
 *
 *  3. МОДИФІКАТОРИ. `format: "webp"` провайдер `vercel` ігнорує (там
 *     формат домовляється по `Accept`, і віддається avif), але
 *     локальний ipx його слухає — без нього в деві картинки лишались би
 *     jpeg. `quality` передаємо ЯВНО: глобальний `image.quality` діє
 *     лише всередині <NuxtImg>, а голий `useImage()` дефолтить на 100.
 * ==================================================================== */

export function useImageOptimizer() {
  const img = useImage();
  const requestURL = useRequestURL();

  // Оптимізатор зламався (не 200) — фолбек лишається САМЕ як мережа
  // безпеки: без нього будь-який його збій дав би биту картинку.
  const failed = ref(false);

  const isIpx = img.options.provider === "ipx";
  // Порт беремо з конфіга, а не з `useRequestURL()`: на сторінках зі
  // `swr` origin приходить без порту (`http://localhost`), ipx стукає в
  // порт 80 і віддає 500 — а ще SSR і клієнт розходяться в `src`, що
  // Vue бачить як hydration mismatch. requestURL лишається запасним.
  const devOrigin = String(useRuntimeConfig().public.devImageOrigin || "");

  function sourceFor(url: string): string {
    if (!url) return "";
    if (!isIpx || !url.startsWith("/")) return url;
    return `${devOrigin || requestURL.origin}${url}`;
  }

  function optimize(
    url: string,
    width: number,
    quality: number,
  ): string | null {
    const src = sourceFor(url);
    if (!src) return null;
    const out = img(src, { width, format: "webp", quality });
    return out === src ? null : out;
  }

  /** Оптимізований URL заданої ширини; при збої — вихідний. */
  function toSrc(
    url: string,
    width: number,
    quality: number = IMAGE_QUALITY,
  ): string {
    if (!url || failed.value) return url || "";
    return optimize(url, width, quality) ?? url;
  }

  /** srcset або undefined, якщо оптимізації немає (пастка 2). */
  function toSrcset(
    url: string,
    widths: number[],
    quality: number = IMAGE_QUALITY,
  ): string | undefined {
    if (!url || failed.value) return undefined;
    const parts: string[] = [];
    for (const w of widths) {
      const out = optimize(url, w, quality);
      if (!out) return undefined;
      parts.push(`${out} ${w}w`);
    }
    return parts.length ? parts.join(", ") : undefined;
  }

  /** Вішати на @error: далі показуємо оригінал і більше не пробуємо. */
  function onError() {
    // Другий шанс лише один: якщо не витягнув і оригінал — здаємось,
    // інакше отримали б цикл на кожному рендері.
    if (!failed.value) failed.value = true;
  }

  return { toSrc, toSrcset, onError, failed };
}
